import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { handleUnauthorized } from '../services/api';
import { setCache, clearAllCache, CACHE_KEYS } from '../services/cacheService';
import i18n from '../i18n';

// Base API URL - this should be configured based on environment

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  department?: string;
  position?: string;
  city?: string;
  mobile_number?: string;
  manager_id?: number;
  signature_image_path?: string;
  is_admin?: boolean;
  is_customer_admin?: boolean;
  customer_id?: number;
  customer_type?: 'individual' | 'business';
  locale?: string;
  avatar_url?: string;
  microsoft_id?: string;
  manager?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    department?: string;
    position?: string;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  
  // Initialize auth state on component mount
  useEffect(() => {
    const authState = checkAuth();
    setUser(authState.user);
    setToken(authState.token);
    setIsAuthenticated(authState.isAuthenticated);
  }, []);
  
  // Pre-load reference data into localStorage cache after login (fire-and-forget)
  const preloadCaches = async (authToken: string) => {
    const headers = {
      'Authorization': `Bearer ${authToken}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };

    // Fetch categories and users in parallel
    const [categoriesRes, usersRes] = await Promise.allSettled([
      fetch(`${API_BASE_URL}/document-categories`, { headers }),
      fetch(`${API_BASE_URL}/users`, { headers }),
    ]);

    if (categoriesRes.status === 'fulfilled' && categoriesRes.value.ok) {
      try {
        const data = await categoriesRes.value.json();
        const categories = data?.categories ?? (Array.isArray(data) ? data : []);
        setCache(CACHE_KEYS.CATEGORIES, categories);
      } catch { /* ignore parse errors */ }
    }

    if (usersRes.status === 'fulfilled' && usersRes.value.ok) {
      try {
        const data = await usersRes.value.json();
        const users = Array.isArray(data) ? data : (data?.data ?? []);
        setCache(CACHE_KEYS.USERS, users);
      } catch { /* ignore parse errors */ }
    }
  };

  const login = async (credentials: LoginCredentials): Promise<AuthState> => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Sending login request to API:', `${API_BASE_URL}/login`);
      
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
          device_name: 'web-browser', // Required by the API
        }),
        credentials: 'include'
      });
      
      const data = await response.json();
      console.log('API login response:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      // Ensure the manager property is properly preserved
      if (data.user && data.user.manager) {
        console.log('Manager data from login response:', data.user.manager);
        console.log('Manager ID:', data.user.manager.id);
      } else {
        console.warn('No manager data found in the login response!');
        if (data.user) {
          console.log('User data structure:', Object.keys(data.user));
          console.log('Manager_id value:', data.user.manager_id);
          
          // If manager_id exists but no manager object, try to fetch the manager data
          if (data.user.manager_id && !data.user.manager) {
            try {
              console.log('Attempting to fetch manager data for manager_id:', data.user.manager_id);
              const managerResponse = await fetch(`${API_BASE_URL}/users/${data.user.manager_id}`, {
                headers: {
                  'Authorization': `Bearer ${data.token}`,
                  'Accept': 'application/json'
                }
              });
              
              if (managerResponse.ok) {
                const managerData = await managerResponse.json();
                console.log('Fetched manager data:', managerData);
                
                // Add manager data to user object
                data.user.manager = managerData;
              }
            } catch (managerErr) {
              console.error('Failed to fetch manager data:', managerErr);
            }
          }
        }
      }
      
      // Sync i18n language with user's saved locale preference
      if (data.user?.locale) {
        i18n.changeLanguage(data.user.locale);
      }

      // Store token and user in localStorage for persistent auth
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Pre-load reference data into cache (fire-and-forget, errors are silently ignored)
      preloadCaches(data.token).catch(() => {});
      
      const authState = {
        user: data.user,
        token: data.token,
        isAuthenticated: true,
      };
      
      // Update state
      setUser(authState.user);
      setToken(authState.token);
      setIsAuthenticated(true);
      
      setLoading(false);
      return authState;
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during login');
      setLoading(false);
      throw err;
    }
  };
  
  const logout = async (): Promise<void> => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        await fetch(`${API_BASE_URL.replace(/\/api\/?$/, '')}/api/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (err) {
        // If the logout request fails, proceed with local logout anyway
        console.error('Logout request failed', err);
      }
    }
    
    // Clear local storage and cached reference data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearAllCache();
    
    // Update state
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };
  
  const checkAuth = (): AuthState => {
    const token = localStorage.getItem('token');
    const userJson = localStorage.getItem('user');
    
    if (token && userJson) {
      try {
        const user = JSON.parse(userJson);
        return {
          user,
          token,
          isAuthenticated: true,
        };
      } catch (err) {
        // Invalid user JSON, clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    return {
      user: null,
      token: null,
      isAuthenticated: false,
    };
  };

  const fetchUserProfile = async (): Promise<User | null> => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        console.log('Fetching user profile with manager data...');
        const response = await fetch(`${API_BASE_URL}/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Profile API response:', data);
          if (data.user) {
            return data.user;
          } else if (data) {
            // Some APIs just return the user object directly
            return data;
          }
        } else if (response.status === 401) {
          handleUnauthorized();
          return null;
        } else {
          const errorData = await response.json();
          console.error('Failed to fetch user profile:', errorData);
          throw new Error('Failed to fetch user profile');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        return null;
      }
    }
    
    return null;
  };
  
  return {
    login,
    logout,
    checkAuth,
    fetchUserProfile,
    loading,
    error,
    user,
    token,
    isAuthenticated
  };
};
